/**
 * Fixture data for the mocked player artifact page. Three versions of a
 * fictional project, showing the progression: v1 trim only, v2 trim +
 * burn_subtitles, v3 trim + burn_subtitles + resize to 9:16 vertical.
 *
 * All video URLs point to Vidstack's public demo CDN (Sprite Fight, a Blender
 * Animation Studio open movie, CC BY 4.0). CORS is served with ACAO=*, so the
 * player renders without any UB backend.
 */

import type { VideoProject } from '../lib/types'

const oneMinAgo = new Date(Date.now() - 60_000).toISOString()
const fiveMinAgo = new Date(Date.now() - 5 * 60_000).toISOString()
const tenMinAgo = new Date(Date.now() - 10 * 60_000).toISOString()

const VIDSTACK_MP4 = 'https://files.vidstack.io/sprite-fight/720p.mp4'
const VIDSTACK_POSTER = 'https://files.vidstack.io/sprite-fight/poster.webp'
const VIDSTACK_THUMBS = 'https://files.vidstack.io/sprite-fight/thumbnails.vtt'
const VIDSTACK_CHAPTERS = 'https://files.vidstack.io/sprite-fight/chapters.vtt'
const VIDSTACK_SUBS_EN = 'https://files.vidstack.io/sprite-fight/subs/english.vtt'

const commonTracks = [
  { src: VIDSTACK_CHAPTERS, kind: 'chapters' as const, language: 'en-US', default: true },
  { src: VIDSTACK_SUBS_EN, kind: 'subtitles' as const, label: 'English', language: 'en-US', default: true },
]

const DEMO_PROJECT: VideoProject = {
  project_id: 'demo',
  source_url: VIDSTACK_MP4,
  created_at: tenMinAgo,
  latest_ready_version: 3,
  versions: [
    {
      version: 1,
      status: 'ready',
      spec_hash: 'sha256:aaa111',
      ops: [{ type: 'trim', start: 10, end: 40 }],
      output_url: VIDSTACK_MP4,
      poster_url: VIDSTACK_POSTER,
      thumbnails_url: VIDSTACK_THUMBS,
      tracks: commonTracks,
      duration_seconds: 30,
      size_bytes: 3_400_000,
      created_at: tenMinAgo,
      ready_at: tenMinAgo,
    },
    {
      version: 2,
      status: 'ready',
      spec_hash: 'sha256:bbb222',
      ops: [
        { type: 'trim', start: 10, end: 40 },
        { type: 'burn_subtitles', srt_url: 'https://example.com/subs.srt', style: { font_size: 24 } },
      ],
      output_url: VIDSTACK_MP4,
      poster_url: VIDSTACK_POSTER,
      thumbnails_url: VIDSTACK_THUMBS,
      tracks: commonTracks,
      duration_seconds: 30,
      size_bytes: 3_800_000,
      created_at: fiveMinAgo,
      ready_at: fiveMinAgo,
    },
    {
      version: 3,
      status: 'ready',
      spec_hash: 'sha256:ccc333',
      ops: [
        { type: 'trim', start: 10, end: 40 },
        { type: 'burn_subtitles', srt_url: 'https://example.com/subs.srt', style: { font_size: 24 } },
        { type: 'resize', aspect: '9:16', crop: 'smart' },
      ],
      output_url: VIDSTACK_MP4,
      poster_url: VIDSTACK_POSTER,
      thumbnails_url: VIDSTACK_THUMBS,
      tracks: commonTracks,
      duration_seconds: 15,
      size_bytes: 2_100_000,
      created_at: oneMinAgo,
      ready_at: oneMinAgo,
    },
  ],
}

const PROJECTS: Record<string, VideoProject> = {
  demo: DEMO_PROJECT,
  // Add more fixtures here to exercise other UI states, e.g. one processing,
  // one failed, one with a single version, etc.
}

export function getMockProject(projectId: string): VideoProject | null {
  return PROJECTS[projectId] ?? null
}

export function listMockProjectIds(): string[] {
  return Object.keys(PROJECTS)
}
